import React from 'react';
import { STUDENT_SECTION } from '../data/content';
import ScrollReveal from '../components/ScrollReveal';
import MagneticButton from '../components/MagneticButton';
import useInView from '../hooks/useInView';
import useMotionPrefs from '../hooks/useMotionPrefs';
import { Spark } from '../components/icons';

/* ==================================================================
   StudentSection
   ------------------------------------------------------------------
   Everything a student already has - resume, skills, projects,
   applications, interview, offer - drifting toward one Opportunity
   card. Chips arrive in sequence, the wires draw themselves, then a
   light keeps travelling along each wire toward the destination.

   Chip positions live here (they are layout, not content) and the
   wires derive their endpoints from the same numbers, so the drawing
   can never drift out of sync with the chips.
   ================================================================== */

const POSITIONS = [
  { x: 0, y: 4 },
  { x: 27, y: 15 },
  { x: 0, y: 36 },
  { x: 27, y: 47 },
  { x: 0, y: 68 },
  { x: 27, y: 79 },
];

const TARGET = { x: 62, y: 50 };

export default function StudentSection() {
  const { reduced } = useMotionPrefs();
  const [ref, inView] = useInView({ threshold: 0.3, skip: reduced });
  const { journey, destination } = STUDENT_SECTION;

  return (
    <section id="students" className="hs-students">
      <div className="hs-shell hs-students__grid">
        <div className="hs-students__copy">
          <ScrollReveal as="p" className="hs-eyebrow">{STUDENT_SECTION.eyebrow}</ScrollReveal>
          <ScrollReveal as="h2" className="hs-h2 hs-h2--tight" delay={80}>
            {STUDENT_SECTION.heading}
          </ScrollReveal>
          <ScrollReveal as="p" className="hs-lede" delay={150}>
            {STUDENT_SECTION.sub}
          </ScrollReveal>
          <ScrollReveal delay={210} variant="fade">
            <MagneticButton to={STUDENT_SECTION.cta.to} variant="primary" size="lg" arrow>
              {STUDENT_SECTION.cta.label}
            </MagneticButton>
          </ScrollReveal>
        </div>

        <div className="hs-journey" ref={ref} data-in={inView ? 'true' : 'false'}>
          <svg className="hs-journey__wires" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {POSITIONS.map((pos, i) => {
              const from = { x: pos.x + 11, y: pos.y + 5 };
              const d = `M${from.x} ${from.y} C ${(from.x + TARGET.x) / 2} ${from.y}, ${(from.x + TARGET.x) / 2} ${TARGET.y}, ${TARGET.x} ${TARGET.y}`;
              return (
                <g key={journey[i].id} style={{ '--hs-i': i }}>
                  <path className="hs-journey__wire" d={d} pathLength="100" vectorEffect="non-scaling-stroke" />
                  <path className="hs-journey__pulse" d={d} pathLength="100" vectorEffect="non-scaling-stroke" />
                </g>
              );
            })}
          </svg>

          {journey.map((item, i) => (
            <span
              className="hs-journey__chip"
              key={item.id}
              style={{ '--hs-x': `${POSITIONS[i].x}%`, '--hs-y': `${POSITIONS[i].y}%`, '--hs-i': i }}
            >
              {item.label}
            </span>
          ))}

          <div className="hs-journey__target">
            <span className="hs-journey__halo" aria-hidden="true" />
            <span className="hs-journey__badge"><Spark size={13} /> Match</span>
            <p className="hs-journey__title">{destination.title}</p>
            <p className="hs-journey__sub">{destination.sub}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
